import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useHotelPartnerCatalogSearch } from "@/components/pages/guesteria-partners/hooks/hotel-partner-curation.hooks";
import { GuesteriaCurationActivityCard } from "@/components/pages/guesteria-partners/components/GuesteriaCurationActivityCard";
import type { PartnerHotelCatalogItem } from "@/types/partner-destination";

export interface GuesteriaAddActivityFromViatorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hotelId: string | undefined;
  cityName?: string;
  alreadyAddedCodes: Set<string>;
  onAdd: (item: PartnerHotelCatalogItem) => void;
}

export const GuesteriaAddActivityFromViatorDialog: React.FC<
  GuesteriaAddActivityFromViatorDialogProps
> = ({ open, onOpenChange, hotelId, cityName, alreadyAddedCodes, onAdd }) => {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState("");

  const { data, isLoading, isDebouncing, isError } =
    useHotelPartnerCatalogSearch(hotelId, searchInput, open);

  let body: React.ReactNode;
  if (isLoading || isDebouncing) {
    body = (
      <div className="text-muted-foreground px-3 py-6 text-center text-sm">
        {t("partners.curation.addDialogLoading")}
      </div>
    );
  } else if (isError) {
    body = (
      <div className="text-destructive px-3 py-6 text-center text-sm">
        {t("partners.curation.loadError")}
      </div>
    );
  } else if (!data?.data.length) {
    body = (
      <div className="text-muted-foreground px-3 py-6 text-center text-sm">
        {t("partners.curation.addDialogEmpty")}
      </div>
    );
  } else {
    body = (
      <div className="flex flex-col gap-2">
        {data.data.map((item) => {
          const isAdded = alreadyAddedCodes.has(item.productCode);
          return (
            <GuesteriaCurationActivityCard
              key={item.productCode}
              title={item.title}
              imageUrl={item.imageUrl}
              rating={item.rating}
              reviewCount={item.reviewCount}
              price={item.price}
              currency={item.currency}
              action={
                isAdded ? (
                  <Button type="button" variant="outline" size="sm" disabled>
                    {t("partners.curation.added")}
                  </Button>
                ) : (
                  <Button type="button" size="sm" onClick={() => onAdd(item)}>
                    {t("partners.curation.add")}
                  </Button>
                )
              }
            />
          );
        })}
      </div>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setSearchInput("");
        onOpenChange(next);
      }}
    >
      <DialogContent
        showCloseButton={false}
        className="flex max-h-[min(90vh,640px)] w-[calc(100%-2rem)] max-w-[480px] flex-col gap-0 overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white p-0 shadow-[0px_3px_3px_0px_rgba(0,0,0,0.12)]"
      >
        <div className="relative flex shrink-0 flex-col border-b border-[#E5E5E5] px-4 pt-4 pb-3">
          <DialogTitle className="text-center text-base leading-normal font-semibold text-[#171717]">
            {cityName
              ? t("partners.curation.addDialogTitle", { city: cityName })
              : t("partners.curation.addFromViator")}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-center text-sm">
            {t("partners.curation.addDialogDescription")}
          </DialogDescription>
          <button
            type="button"
            className="absolute top-3 right-3 rounded p-1 text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-900"
            onClick={() => onOpenChange(false)}
            aria-label={t("partners.curation.close")}
          >
            <X className="size-5" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-4">
          <div className="relative">
            <Input
              placeholder={t("partners.curation.addDialogSearchPlaceholder")}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              autoComplete="off"
              className="h-9 border-[#E5E5E5] pr-10 text-sm text-[#171717] shadow-none placeholder:text-[#737373] focus-visible:border-[#E5E5E5] focus-visible:ring-0"
            />
            <span
              className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-[#737373]"
              aria-hidden
            >
              <Search className="size-4" />
            </span>
          </div>

          {body}
        </div>

        <div className="flex shrink-0 items-center justify-end border-t border-[#E5E5E5] px-4 py-3">
          <Button
            type="button"
            variant="outline"
            className="h-8 rounded-md border-[#E5E5E5] bg-white px-4 text-sm font-medium text-zinc-900 shadow-none hover:bg-zinc-50"
            onClick={() => onOpenChange(false)}
          >
            {t("partners.curation.close")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
